import { For, Show, type JSX } from "solid-js";
import type { Tab, StatusResponse, AdminControlPlaneSnapshot } from "../shared";
import type { SystemOperatorSession } from "../types";
import { TargetProfilePicker } from "./TargetProfilePicker";
import { getAdminRuntimeConfig } from "../runtime";

const TABS: { id: Tab; label: string; requires?: "directDb" }[] = [
  { id: "dashboard", label: "Dashboard" },
  { id: "users", label: "Users" },
  { id: "projects", label: "Projects" },
  { id: "auth", label: "Auth" },
  { id: "organizations", label: "Orgs" },
  { id: "registration-requests", label: "Org Requests" },
  { id: "maintenance", label: "Maintenance" },
  { id: "task-queues", label: "Task Queue" },
  { id: "errors", label: "Errors" },
  { id: "performance", label: "Performance" },
  { id: "database", label: "Database", requires: "directDb" },
  { id: "realtime", label: "Realtime" },
];

export function Layout(props: {
  tab: Tab;
  onTab: (t: Tab) => void;
  env: string;
  status: StatusResponse | null;
  controlPlane: AdminControlPlaneSnapshot | null;
  operatorSession: SystemOperatorSession | null;
  onLogout: () => Promise<void>;
  onServerStart: (name: string, port?: number) => void;
  onServerStop: (name: string) => void;
  children: JSX.Element;
}) {
  const runtime = () => getAdminRuntimeConfig();
  const visibleTabs = () =>
    TABS.filter((tab) => {
      if (tab.requires === "directDb") {
        return runtime().controlPlane.supportsDirectDbAccess ?? true;
      }
      return true;
    });

  return (
    <div class="flex h-screen flex-col">
      <header class="shrink-0 border-b border-gray-800 bg-gray-900">
        <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-6 px-3 sm:px-6 py-2 sm:py-3">
          <div class="min-w-0">
            <div class="flex items-center gap-4">
              <h1 class="text-lg font-bold text-cyan-400">SearchGrid Admin</h1>
              <p class="hidden text-sm text-gray-500 lg:block">Operate local, VPS, and Cloudflare environments from one dashboard.</p>
            </div>
          </div>

          <div class="flex flex-wrap sm:flex-nowrap shrink-0 items-center gap-2 sm:gap-3">
            <TargetProfilePicker compact />
            <Show when={props.operatorSession}>
              {(session) => (
                <div class="flex items-center gap-3">
                  <div class="hidden sm:flex flex-col items-end">
                    <span class="text-sm font-medium text-gray-100">{session().operator.displayName}</span>
                    <span class="text-xs text-gray-500">{session().operator.email}</span>
                  </div>
                  <button
                    onClick={() => void props.onLogout()}
                    class="cursor-pointer rounded-lg border border-gray-700 px-3 py-1.5 text-xs font-semibold text-gray-200 transition-colors hover:bg-gray-800"
                  >
                    Log Out
                  </button>
                </div>
              )}
            </Show>
          </div>
        </div>

        <div class="w-full border-t border-gray-800 bg-gray-900/70 px-3 sm:px-6">
          <nav class="flex w-full gap-1 overflow-x-auto py-3">
            <For each={visibleTabs()}>
              {(t) => (
                <button
                  onClick={() => props.onTab(t.id)}
                  class={`shrink-0 cursor-pointer rounded-t px-4 py-2 text-sm font-medium transition-colors ${props.tab === t.id ? "tab-active bg-gray-800" : "text-gray-400 hover:bg-gray-800/50 hover:text-gray-200"
                    }`}
                >
                  {t.label}
                </button>
              )}
            </For>
          </nav>
        </div>
      </header>
      <main class="flex-1 overflow-auto p-6">{props.children}</main>
    </div>
  );
}
